import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, MapPin, Milestone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import AgreementsShell from "@/components/agreements/AgreementsShell";
import DocumentCard from "@/components/agreements/DocumentCard";
import { ROADMAP, STAGES, STAGE_BY_ID } from "@/data/agreements/stages";
import { DOC_BY_ID } from "@/data/agreements/documentLibrary";
import { useAgreementProject } from "@/hooks/useAgreementProject";

/**
 * LegalRoadmap — route: /agreements/roadmap
 *
 * THE CPG LEGAL ROADMAP: seventeen steps in the order a brand meets them, from "before you tell
 * anyone" to "diligence will surface every agreement you never signed".
 */

const stepDomId = (id) => `roadmap-step-${id}`;

export default function LegalRoadmap() {
  const { intake, hasIntake } = useAgreementProject();
  const currentStage = intake?.stage;

  const steps = useMemo(
    () =>
      ROADMAP.map((step) => ({
        ...step,
        docs: (step.documents || []).map((id) => DOC_BY_ID[id]).filter(Boolean),
      })),
    [],
  );

  const firstStepOfCurrentStage = useMemo(
    () => steps.find((s) => s.stage === currentStage)?.id || null,
    [steps, currentStage],
  );

  const stagesWithSteps = useMemo(() => {
    const seen = new Map();
    for (const s of steps) if (!seen.has(s.stage)) seen.set(s.stage, s.id);
    return STAGES.filter((stage) => seen.has(stage.id)).map((stage) => ({ ...stage, firstStepId: seen.get(stage.id) }));
  }, [steps]);

  const jumpTo = (stepId) => {
    const el = typeof document !== "undefined" ? document.getElementById(stepDomId(stepId)) : null;
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <AgreementsShell
      wide
      crumbs={[{ label: "Roadmap" }]}
      title="The CPG legal roadmap"
      subtitle={`${steps.length} steps, in the order a packaged-goods brand actually meets them. Each one names the agreements to have in hand, and the thing that most often goes wrong at that point.`}
      actions={
        <Button asChild variant="outline">
          <Link to={hasIntake ? "/agreements/plan" : "/agreements/intake"}>
            {hasIntake ? "My plan" : "Build my plan"}
          </Link>
        </Button>
      }
    >
      {/* Jump to stage */}
      <div className="mb-6 rounded-xl border bg-card p-3">
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Jump to a stage</p>
        <div className="flex flex-wrap gap-1.5">
          {stagesWithSteps.map((stage) => {
            const isCurrent = stage.id === currentStage;
            return (
              <button
                key={stage.id}
                type="button"
                onClick={() => jumpTo(stage.firstStepId)}
                className={cn(
                  "rounded-full border px-3 py-1 text-xs font-medium hover:bg-muted",
                  isCurrent ? "border-indigo-500 bg-indigo-600 text-white hover:bg-indigo-700" : "text-muted-foreground",
                )}
              >
                {stage.name}
                {isCurrent && <span className="ml-1.5">· you</span>}
              </button>
            );
          })}
        </div>
        {!currentStage && (
          <p className="mt-2 text-xs text-muted-foreground">
            <Link className="underline" to="/agreements/intake">
              Tell us your stage
            </Link>{" "}
            and we will mark where you are on the map.
          </p>
        )}
      </div>

      {/* Timeline */}
      <div className="relative">
        {/* Connecting spine (desktop only) */}
        <span aria-hidden className="absolute left-[19px] top-6 hidden h-[calc(100%-3rem)] w-px bg-border md:block" />

        <ol className="space-y-4 sm:space-y-5">
          {steps.map((step) => {
          const isHere = Boolean(currentStage) && step.stage === currentStage;
          const showPill = isHere && step.id === firstStepOfCurrentStage;
          const stage = STAGE_BY_ID[step.stage];
          return (
            <li key={step.id} id={stepDomId(step.id)} className="relative scroll-mt-4 md:pl-12">
              {/* Node */}
              <span
                aria-hidden
                className={cn(
                  "absolute left-0 top-1 z-10 hidden h-10 w-10 items-center justify-center rounded-full border-2 bg-background text-sm font-semibold tabular-nums md:inline-flex",
                  isHere ? "border-indigo-600 text-indigo-700" : "border-border text-muted-foreground",
                )}
              >
                {step.step}
              </span>

              <Card className={cn("overflow-hidden", isHere && "ring-2 ring-indigo-500 ring-offset-2")}>
                <CardContent className="p-4 sm:p-5">
                  <div className="flex flex-wrap items-center gap-2">
                    {/* Mobile number badge */}
                    <span className="inline-flex h-7 w-7 items-center justify-center rounded-full border text-xs font-semibold tabular-nums md:hidden">
                      {step.step}
                    </span>
                    <h2 className="text-base font-semibold sm:text-lg">{step.title}</h2>
                    {stage && (
                      <Badge variant="outline" className="font-normal">
                        {stage.name}
                      </Badge>
                    )}
                    {showPill && (
                      <Badge className="gap-1 bg-indigo-600 font-normal text-white hover:bg-indigo-600">
                        <MapPin className="h-3 w-3" /> You are here
                      </Badge>
                    )}
                  </div>

                  {step.summary && <p className="mt-2 text-sm text-muted-foreground">{step.summary}</p>}

                  {step.watchOut && (
                    <div className="mt-3 flex gap-2.5 rounded-lg border border-pink-200 bg-pink-50 p-3 text-xs leading-relaxed text-pink-900">
                      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-pink-600" />
                      <span>
                        <strong className="font-semibold">Watch out. </strong>
                        {step.watchOut}
                      </span>
                    </div>
                  )}

                  {step.docs.length > 0 ? (
                    <div className="mt-4">
                      <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                        Agreements at this step
                      </p>
                      <div className="grid gap-2 sm:grid-cols-2">
                        {step.docs.map((doc) => (
                          <DocumentCard key={doc.id} doc={doc} compact />
                        ))}
                      </div>
                    </div>
                  ) : (
                    <p className="mt-4 text-xs text-muted-foreground">
                      No library entries are wired to this step yet.
                    </p>
                  )}
                </CardContent>
              </Card>
            </li>
          );
          })}
        </ol>
      </div>

      <div className="mt-8 flex flex-col items-start gap-3 rounded-xl border bg-card p-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="flex items-start gap-2 text-sm text-muted-foreground">
          <Milestone className="mt-0.5 h-4 w-4 shrink-0 text-indigo-600" />
          The roadmap is the general path. Your plan is the specific one — built from your answers, not from a stage.
        </p>
        <Button asChild className="bg-indigo-600 hover:bg-indigo-700">
          <Link to={hasIntake ? "/agreements/plan" : "/agreements/intake"}>
            {hasIntake ? "Open my plan" : "Answer the intake"}
          </Link>
        </Button>
      </div>
    </AgreementsShell>
  );
}
